import { useCallback, useRef, useState } from 'preact/hooks';
import { useResizeObserver } from '../hooks/UNSAFE_useResizeObserver';
import { BaseSwatchView, getColCount } from '../PRIVATE_BaseSwatchView';
import { BaseSwatchViewPublicProps as Props } from './BaseSwatchView.types';
import { forwardRef, ForwardedRef, useImperativeHandle } from 'preact/compat';

type SwatchFlexViewProps<K extends string | number, D> = Props<K, D>;
type SizeInfo = {
  swatchWidth?: number;
  swatchHeight?: number;
  containerWidth?: number;
};

export const SwatchFlexView = forwardRef(
  <K extends string | number, D>(
    props: SwatchFlexViewProps<K, D>,
    ref?: ForwardedRef<HTMLDivElement>
  ) => {
    const rootRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref!, () => rootRef.current!, [rootRef]);

    const [size, setSize] = useState<SizeInfo>({});
    const updateSwatchSize = (swatchWidth: number, swatchHeight: number) => {
      setSize((size: SizeInfo) => ({ ...size, swatchWidth, swatchHeight }));
    };

    const handleResize = useCallback(
      (entry: ResizeObserverEntry) => {
        const containerWidth = entry.contentRect.width;
        if (size.containerWidth !== containerWidth) {
          setSize((size: SizeInfo) => ({ ...size, containerWidth }));
        }
      },
      [size]
    );
    useResizeObserver(rootRef, handleResize);

    const columns = getColCount(8, size.swatchWidth, size.containerWidth);
    const swatchSize = { width: size.swatchWidth, height: size.swatchHeight };

    return (
      <BaseSwatchView
        {...props}
        ref={rootRef}
        columns={columns}
        swatchSize={swatchSize}
        updateSwatchSize={updateSwatchSize}
      />
    );
  }
);
